import { DateTime } from "luxon";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useHistory } from "react-router-dom";

import { Button, CircularProgress, Divider, Grid, IconButton, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import { SectionGridContainer } from "../../components/Layout";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { FleetContext, RiderContext } from "../../context";
import GroupReceipt from "./GroupReceipt";
import Table from "../../components/Layout/Table";
import { StringUtils } from "../../util";
const { formatNumber, uuidFromUrl } = StringUtils;

const ReservationDetail = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const { riderId, groupReservationId } = useParams();
  const { setActiveFleet } = useContext(FleetContext);
  const {
    groupReservations,
    groupReceipt,
    activeGroupReservation,
    setActiveGroupReservation,
    fetchFleet,
    fetchGroupReceipt,
  } = useContext(RiderContext);
  const [loading, setLoading] = useState(false);
  const lineItemsSeen = new Set();

  useEffect(() => {
    const waitForRes = async () => {
      // For detail page we always want to hit db for fresh data
      try {
        setLoading(true);
        await Promise.all([
          setActiveGroupReservation(groupReservationId),
          fetchGroupReceipt(groupReservationId),
        ]);
      } catch (err) {
        setLoading(false);
      }
      setLoading(false);
    };
    if (groupReservationId) waitForRes();
    return async () => {
      await setActiveGroupReservation(null);
      await fetchGroupReceipt(null);
    };
  }, [groupReservationId]);

  useEffect(() => {
    if (activeGroupReservation?.reservations[0]) {
      fetchFleet(activeGroupReservation.reservations[0].fleets[0]);
    }
    return () => fetchFleet(null);
  }, [activeGroupReservation]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!activeGroupReservation) {
    return (
      <Typography variant="body1">{t("Riders.GroupReservationDetail.RequestError")}</Typography>
    );
  }

  const columns = [
    {
      canonicalName: "start_end",
      name: "Start / End",
      renderCellContent: ({ begin, start, stop }) => {
        const formattedStart = DateTime.fromISO(begin || start).toFormat("HH:mm");
        const formattedEnd = DateTime.fromISO(stop).toFormat("HH:mm");
        return `${begin || start ? formattedStart : ""} / ${stop ? formattedEnd : ""}`;
      },
      tableCellProps: { style: { padding: theme.spacing(1) } },
    },
    {
      canonicalName: "vehicle",
      name: "Vehicle",
      renderCellContent: (reservation) => {
        const { vehicle, fleets, shortcode } = reservation;
        const fleetId = fleets[0];
        const vehicleId = uuidFromUrl(vehicle);
        const vehicleURL = `/fleets/${fleetId}/vehicles/${vehicleId}`;

        return (
          <Button
            onClick={() => {
              setActiveFleet(fleetId);
              history.push(vehicleURL);
            }}
            to={vehicleURL}
          >
            {shortcode.split("/").at(-1)}
          </Button>
        );
      },
    },
    {
      canonicalName: "contact",
      name: "Contact",
      renderCellContent: (reservation) => reservation?.guest?.phone_number ?? "Host",
      tableCellProps: { style: { padding: theme.spacing(1) } },
    },
    {
      canonicalName: "fare",
      name: "Fare",
      renderCellContent: (reservation) => {
        const lineItem = groupReceipt?.line_items.find(
          (item) =>
            item.vehicle_shortcode === reservation.shortcode.split("/").at(-1) &&
            !lineItemsSeen.has(item.id),
        );
        if (!lineItem) return <CircularProgress size={20} color="inherit" />;
        lineItemsSeen.add(lineItem.id);
        return formatNumber(lineItem?.total_amount, groupReceipt?.currencyLocal);
      },
      tableCellProps: { style: { padding: theme.spacing(1) } },
    },
    {
      canonicalName: "view",
      name: "",
      renderCellContent: (reservation) => (
        <RouterLink to={`/riders/${riderId}/reservations/${reservation.id}`}>
          <Button>{t("Riders.GroupReservationDetail.Buttons.ViewReservation")}</Button>
        </RouterLink>
      ),
    },
  ];

  return (
    <SectionGridContainer
      spaceContents
      style={{ paddingLeft: 0, paddingRight: 0 }}
      gridProps={{ alignItems: "flex-start" }}
    >
      <Grid item container xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Grid item style={{ paddingLeft: 10 }}>
          <IconButton
            edge="start"
            component={RouterLink}
            to={{
              pathname: `/riders/${riderId}/group-reservations`,
              state: { page: groupReservations.currentPage },
            }}
            style={{ marginTop: theme.spacing(-1) }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <Typography data-testid="group-rides-detail-title" variant="h5">
            {t("Riders.GroupReservationDetail.GroupRide")}:{" "}
            {activeGroupReservation?.hostReservation?.start &&
              DateTime.fromISO(activeGroupReservation.hostReservation.start).toLocaleString(
                DateTime.DATETIME_MED_WITH_WEEKDAY,
              )}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, marginTop: theme.spacing(-1) }}
        >
          <Divider />
        </Grid>
      </Grid>
      <Grid item container xs={12} md={4}>
        <GroupReceipt />
      </Grid>
      <Grid item container xs={12} md={8} spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: theme.spacing(4) }}>
          <Typography variant="h5">
            {t("Riders.GroupReservationDetail.GroupReservations")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Table columns={columns} rows={activeGroupReservation?.reservations} hidePagination />
        </Grid>
      </Grid>
    </SectionGridContainer>
  );
};

export default ReservationDetail;
